/**
 * Colonnes configurables d'un tableau de Service d'exception.
 *
 * Chaque tableau porte sa propre liste de colonnes : l'utilisateur en ajoute, en renomme, en masque.
 * Les colonnes historiques (Client, Heure, Adultes, Enfants, et les six moyens de paiement) pointent
 * vers les champs SQL déjà existants de `exception_service_reservations` via `field` ; les colonnes
 * créées par l'utilisateur rangent leur valeur dans `customValues`, indexée par l'id de la colonne.
 *
 * C'est ce qui garantit qu'aucun montant n'est perdu à la migration : rien n'est déplacé. Un tableau
 * existant reçoit simplement une description de colonnes qui décrit ce qu'il contenait déjà.
 */

export type ColumnType = 'texte' | 'nombre' | 'montant' | 'heure' | 'choix' | 'personne';

export type BoardColumn = {
    /** Stable, généré à la création — sert de clé dans `customValues`, ne change jamais au renommage. */
    id: string;
    label: string;
    type: ColumnType;
    visible: boolean;
    /** Colonne indispensable : ni supprimable, ni masquable. */
    locked: boolean;
    /** Valeurs proposées pour une colonne `choix`. */
    options?: string[];
    /**
     * Champ SQL historique porté par cette colonne (`cb`, `adults`...). Absent = colonne créée par
     * l'utilisateur, dont la valeur vit dans `customValues[id]`.
     */
    field?: string;
    /**
     * Colonne `nombre` comptée comme des couverts : sa valeur est multipliée par ce tarif pour le CA
     * théorique. C'est ce qui remplace le calcul autrefois codé en dur sur adultes/enfants.
     */
    tarifField?: 'tarifAdult' | 'tarifChild';
    /** Colonne `personne` : quelle liste du tableau alimente le sélecteur. */
    personField?: 'takenBy' | 'cashedBy';
};

/** Les quatre colonnes sans lesquelles une ligne de réservation ne veut rien dire. */
const LOCKED_COLUMNS: BoardColumn[] = [
    { id: 'client', label: 'Client', type: 'texte', visible: true, locked: true, field: 'client' },
    { id: 'time', label: 'Heure', type: 'heure', visible: true, locked: true, field: 'time' },
    { id: 'adults', label: 'Adultes', type: 'nombre', visible: true, locked: true, field: 'adults', tarifField: 'tarifAdult' },
    { id: 'children', label: 'Enfants', type: 'nombre', visible: true, locked: true, field: 'children', tarifField: 'tarifChild' },
];

/**
 * Les six moyens de paiement qui étaient jusqu'ici codés en dur dans le module. Ils gardent leur champ
 * SQL : un tableau déjà rempli retrouve ses montants sans qu'une seule valeur ne soit recopiée.
 */
export const LEGACY_PAYMENT_COLUMNS: BoardColumn[] = [
    { id: 'cb', label: 'CB', type: 'montant', visible: true, locked: false, field: 'cb' },
    { id: 'amex', label: 'Amex', type: 'montant', visible: true, locked: false, field: 'amex' },
    { id: 'cash', label: 'Espèces', type: 'montant', visible: true, locked: false, field: 'cash' },
    { id: 'cheque', label: 'Chèque', type: 'montant', visible: true, locked: false, field: 'cheque' },
    { id: 'zen', label: 'Zenchef', type: 'montant', visible: true, locked: false, field: 'zen' },
    { id: 'virm', label: 'Virement', type: 'montant', visible: true, locked: false, field: 'virm' },
];

/**
 * Une seule colonne pour les deux noms, empilés dans la même cellule (« François » au-dessus,
 * « Philippe » en dessous) — c'est ce que montre la maquette 16b. Deux colonnes distinctes doublaient
 * la largeur pour deux valeurs qui se lisent toujours ensemble.
 */
/**
 * LE COMMENTAIRE ÉTAIT SAISI, STOCKÉ, CHERCHABLE... ET INVISIBLE.
 *
 * `exception_service_reservations.comment` existe depuis toujours, la fenêtre de réservation le
 * propose sur toute la hauteur, et la recherche du tableau fouille dedans — mais aucune colonne ne
 * l'affichait, donc personne ne relisait jamais ce qui y avait été noté. Remise à la demande d'Emeni
 * (19/09). Elle reste masquable et déplaçable comme les autres.
 */
const COMMENT_COLUMN: BoardColumn = {
    id: 'comment', label: 'Commentaire', type: 'texte', visible: true, locked: false, field: 'comment',
};

const STAFF_COLUMN: BoardColumn = {
    id: 'staff', label: 'Pris / Encaissé par', type: 'personne', visible: true, locked: false,
};

/**
 * MÊME HISTOIRE QUE LE COMMENTAIRE, POUR DEUX DATES SAISIES À LA MAIN.
 *
 * `created_date` (« Date de réservation ») et `paid_date` (« Date de paiement ») sont proposées sur
 * toute réservation depuis toujours (NewReservationModal), mais n'avaient jamais de colonne pour les
 * afficher dans la grille ni dans l'export Excel — seul le vieil export PDF, resté figé depuis avant les
 * colonnes configurables, les montrait encore. Signalé par Emeni sur le tableau archivé de Brasserie
 * Polpo (25/09) : remises en colonnes à part entière, comme le Commentaire.
 */
const CREATED_DATE_COLUMN: BoardColumn = {
    id: 'created_date', label: 'Date de réservation', type: 'texte', visible: true, locked: false, field: 'created_date',
};
const PAID_DATE_COLUMN: BoardColumn = {
    id: 'paid_date', label: 'Date de paiement', type: 'texte', visible: true, locked: false, field: 'paid_date',
};

/**
 * Colonnes d'un tableau CRÉÉ À PARTIR DE MAINTENANT : seulement les trois moyens de paiement courants,
 * plus un Menu. Les six moyens historiques restent disponibles pour les tableaux déjà en base (voir
 * `legacyBoardColumns`), mais un nouveau service n'a pas à naître avec Amex et Chèque déjà à l'écran.
 */
export const DEFAULT_BOARD_COLUMNS: BoardColumn[] = [
    ...LOCKED_COLUMNS,
    CREATED_DATE_COLUMN,
    PAID_DATE_COLUMN,
    { id: 'cb', label: 'CB', type: 'montant', visible: true, locked: false, field: 'cb' },
    { id: 'cash', label: 'Espèces', type: 'montant', visible: true, locked: false, field: 'cash' },
    { id: 'virm', label: 'Virement', type: 'montant', visible: true, locked: false, field: 'virm' },
    { id: 'menu', label: 'Menu', type: 'choix', visible: true, locked: false, options: [] },
    COMMENT_COLUMN,
    STAFF_COLUMN,
];

/**
 * Colonnes reconstituées pour un tableau qui existait AVANT les colonnes configurables : les six moyens
 * de paiement y sont tous présents, puisque la grille les affichait tous.
 */
export const legacyBoardColumns = (): BoardColumn[] => [
    ...LOCKED_COLUMNS,
    CREATED_DATE_COLUMN,
    PAID_DATE_COLUMN,
    ...LEGACY_PAYMENT_COLUMNS,
    { id: 'menu', label: 'Menu', type: 'choix', visible: false, locked: false, options: [] },
    COMMENT_COLUMN,
    STAFF_COLUMN,
];

/** Hôte d'une valeur de colonne — une réservation, sans que ce module ait à connaître son type complet. */
type ColumnValueHost = Record<string, any> & { customValues?: Record<string, any> };

/**
 * Lit la valeur d'une colonne sur une réservation, qu'elle vive dans un champ SQL historique ou dans le
 * JSON des colonnes ajoutées. Un seul point de lecture pour la grille, les totaux et l'export — sinon
 * les trois finissent par diverger sur les colonnes créées à la main.
 */
export function getColumnValue(reservation: ColumnValueHost, column: BoardColumn): any {
    if (column.id === 'client') return `${reservation.lastName || ''} ${reservation.firstName || ''}`.trim();
    // La colonne d'équipe porte deux valeurs : à l'export elles se lisent « Pris / Encaissé », à l'écran
    // elles s'empilent (voir la cellule dédiée de la grille).
    if (column.id === 'staff') {
        return [reservation.takenBy, reservation.cashedBy].filter(Boolean).join(' / ');
    }
    // Affichées en jj/mm/aaaa plutôt que l'ISO brut stocké en base — même règle que `formatDate` de
    // types.ts, redupliquée ici pour éviter un import circulaire (types.ts importe déjà ce module).
    if (column.id === 'created_date' || column.id === 'paid_date') {
        const iso = reservation[columnFieldKey(column)];
        if (!iso) return '';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    }
    if (column.field) return reservation[columnFieldKey(column)];
    return (reservation.customValues || {})[column.id];
}

/** Version numérique, pour les colonnes `nombre` et `montant` (une cellule vide vaut 0, jamais NaN). */
export function getColumnNumber(reservation: ColumnValueHost, column: BoardColumn): number {
    const raw = getColumnValue(reservation, column);
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
}

/**
 * Les champs SQL sont en snake_case côté base mais camelCase une fois passés par `mapReservationFromApi`
 * — `taken_by` devient `takenBy`. La colonne porte le nom SQL ; la lecture se fait sur l'objet React.
 */
const columnFieldKey = (column: BoardColumn): string =>
    (column.field || '').replace(/_([a-z])/g, (_, c) => c.toUpperCase());

/** Colonnes qui comptent dans l'encaissé : toutes les colonnes `montant` visibles, jamais une liste figée. */
export const amountColumns = (columns: BoardColumn[]): BoardColumn[] =>
    columns.filter((col) => col.type === 'montant' && col.visible);

/** Colonnes `nombre` désignées comme couverts (porteuses d'un tarif). */
export const coverColumns = (columns: BoardColumn[]): BoardColumn[] =>
    columns.filter((col) => col.type === 'nombre' && col.visible && !!col.tarifField);

/** Colonnes dont le total a un sens en pied de grille : les nombres et les montants. */
export const totalledColumns = (columns: BoardColumn[]): BoardColumn[] =>
    columns.filter((col) => (col.type === 'nombre' || col.type === 'montant') && col.visible);

/** Somme encaissée d'une réservation : toutes ses colonnes `montant` visibles. */
export const reservationEncaisse = (reservation: ColumnValueHost, columns: BoardColumn[]): number =>
    amountColumns(columns).reduce((sum, col) => sum + getColumnNumber(reservation, col), 0);

/** CA théorique d'une réservation : chaque colonne de couverts multipliée par son tarif. */
export const reservationTheorique = (reservation: ColumnValueHost, columns: BoardColumn[]): number =>
    coverColumns(columns).reduce(
        (sum, col) => sum + getColumnNumber(reservation, col) * (Number(reservation[col.tarifField!]) || 0),
        0,
    );

/** Reste dû d'une réservation — jamais négatif à l'affichage, un trop-perçu n'est pas une dette. */
export const reservationReste = (reservation: ColumnValueHost, columns: BoardColumn[]): number =>
    Math.max(0, reservationTheorique(reservation, columns) - reservationEncaisse(reservation, columns));

/** Identifiant stable pour une colonne créée à la main : lisible, sans collision avec les ids réservés. */
export function generateColumnId(label: string, existing: BoardColumn[]): string {
    const base = label.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'colonne';
    const taken = new Set(existing.map((col) => col.id));
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base}_${n}`)) n += 1;
    return `${base}_${n}`;
}

/**
 * Relit la liste de colonnes venue de l'API. Un tableau sans colonnes enregistrées (créé avant ce
 * chantier) reçoit la description de ce qu'il affichait déjà, pas les colonnes par défaut d'un tableau
 * neuf : sinon ses montants Amex et Chèque disparaîtraient de l'écran alors qu'ils sont toujours en base.
 */
export function parseBoardColumns(raw: any): BoardColumn[] {
    let parsed: any = raw;
    if (typeof raw === 'string') {
        try { parsed = JSON.parse(raw || 'null'); } catch { parsed = null; }
    }
    if (!Array.isArray(parsed) || parsed.length === 0) return legacyBoardColumns();
    const colonnes = parsed.filter((col: any) => col && typeof col.id === 'string').map((col: any) => ({
        id: col.id,
        label: String(col.label ?? col.id),
        type: (['texte', 'nombre', 'montant', 'heure', 'choix', 'personne'].includes(col.type) ? col.type : 'texte') as ColumnType,
        visible: col.visible !== false,
        locked: !!col.locked,
        ...(Array.isArray(col.options) ? { options: col.options.map(String) } : {}),
        ...(col.field ? { field: String(col.field) } : {}),
        ...(col.tarifField ? { tarifField: col.tarifField } : {}),
        ...(col.personField ? { personField: col.personField } : {}),
    }));
    // Un tableau enregistré avant le retour de la colonne Commentaire ne la connaît pas : on l'ajoute
    // à la relecture, sinon le commentaire resterait invisible sur tous les services déjà créés. Elle
    // se place avant l'équipe, qui se lit toujours en dernier.
    if (!colonnes.some((col) => col.id === 'comment' || col.field === 'comment')) {
        const rangEquipe = colonnes.findIndex((col) => col.id === 'staff');
        if (rangEquipe >= 0) colonnes.splice(rangEquipe, 0, { ...COMMENT_COLUMN });
        else colonnes.push({ ...COMMENT_COLUMN });
    }
    // Même chose pour Date de réservation / Date de paiement (25/09) : un tableau déjà personnalisé
    // avant leur retour ne les connaît pas non plus. Insérées juste après les colonnes verrouillées.
    const rangApresVerrouillees = colonnes.reduce((r, col, i) => (col.locked ? i + 1 : r), 0);
    if (!colonnes.some((col) => col.id === 'created_date' || col.field === 'created_date')) {
        colonnes.splice(rangApresVerrouillees, 0, { ...CREATED_DATE_COLUMN });
    }
    if (!colonnes.some((col) => col.id === 'paid_date' || col.field === 'paid_date')) {
        colonnes.splice(rangApresVerrouillees + 1, 0, { ...PAID_DATE_COLUMN });
    }
    return colonnes;
}
